/**
 * StrataWorks API client for the dashboard.
 *
 * Auth: Entra OIDC. After /api/auth/callback the API sets an HttpOnly cookie
 * "strataworks_session". This client always sends credentials.include so subsequent
 * requests carry the cookie automatically. There is no Bearer-token mode here.
 */

const API_BASE_URL =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
    (import.meta.env.DEV ? 'http://localhost:8000' : '');

export class ApiError extends Error {
    status: number;
    detail: unknown;
    constructor(status: number, message: string, detail?: unknown) {
        super(message);
        this.status = status;
        this.detail = detail;
    }
    get isOffline() {
        return this.status === 0;
    }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (!headers.has('Content-Type') && init.body) {
        headers.set('Content-Type', 'application/json');
    }
    let res: Response;
    try {
        res = await fetch(`${API_BASE_URL}${path}`, {
            ...init,
            headers,
            credentials: 'include',
        });
    } catch (e) {
        throw new ApiError(0, 'network unavailable', e);
    }
    if (res.status === 204) return undefined as T;
    const contentType = res.headers.get('content-type') ?? '';
    const body = contentType.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok) {
        const message =
            typeof body === 'object' && body && 'detail' in body
                ? String(body.detail)
                : `request failed: ${res.status}`;
        throw new ApiError(res.status, message, body);
    }
    return body as T;
}

export interface MeResponse {
    kind: 'user' | 'device';
    user_id?: string;
    device_id?: string;
    email?: string;
    full_name?: string | null;
    org_id: string;
    role: string;
}

export const auth = {
    me: () => request<MeResponse>('/api/auth/me'),
    logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
    loginUrl: () =>
        import.meta.env.VITE_DEV_AUTH_BYPASS === '1'
            ? `${API_BASE_URL}/api/auth/dev-login`
            : `${API_BASE_URL}/api/auth/login`,
    /** Provision a new tablet pairing code (supervisor-only). */
    provisionDevice: (body: { label: string; site_id?: string }) =>
        request<{ device_id: string; pairing_code: string }>('/api/auth/device/provision', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
};

export interface ShiftRow {
    id: string;
    org_id: string;
    user_id: string;
    user_kind: string;
    app_slug: string;
    shift_data: Record<string, unknown>;
    status: string;
    submitted_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface ShiftUpsertPayload {
    id: string;
    app_slug: 'operator' | 'dashboard';
    shift_data: unknown;
    status?: string;
    submitted_at?: string | null;
}

export const shifts = {
    list: (params: { app_slug?: string; limit?: number } = {}) => {
        const qs = new URLSearchParams();
        if (params.app_slug) qs.set('app_slug', params.app_slug);
        if (params.limit) qs.set('limit', String(params.limit));
        const suffix = qs.toString() ? `?${qs}` : '';
        return request<ShiftRow[]>(`/api/shifts${suffix}`);
    },
    upsert: (payload: ShiftUpsertPayload) =>
        request<ShiftRow>('/api/shifts', {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    /** One-shot batch upsert. Backend caps at 1000 shifts per call. */
    bulkUpsert: (shifts: ShiftUpsertPayload[]) =>
        request<{ upserted: number }>('/api/shifts/bulk', {
            method: 'POST',
            body: JSON.stringify({ shifts }),
        }),
};

export const orgs = {
    me: () =>
        request<{
            id: string;
            name: string;
            slug: string;
            logo_url: string | null;
            enabled_apps: string[];
        }>('/api/orgs/me'),
};

export interface DeviceRow {
    id: string;
    label: string;
    site_id: string | null;
    pairing_code: string | null;
    revoked: boolean;
    last_seen_at: string | null;
    created_at: string;
}

export const devices = {
    list: () => request<DeviceRow[]>('/api/devices'),
    revoke: (deviceId: string) =>
        request<DeviceRow>(`/api/devices/${deviceId}/revoke`, { method: 'POST' }),
};

export interface UserRow {
    id: string;
    email: string;
    full_name: string | null;
    role: string;
    site_ids: string[];
    created_at: string;
}

export const users = {
    list: () => request<UserRow[]>('/api/users'),
};

export interface AssetRow {
    id: string;
    org_id: string;
    serial_number: string;
    type: 'drill-bit' | 'hammer' | 'drill-rod';
    manufacturer: string;
    bit_diameter: number | null;
    description: string;
    site_id: string | null;
    status: 'active' | 'decommissioned' | 'in-transit' | 'regrind';
    total_metres_drilled: number;
    notes: string;
    bit_type: string | null;
    face_design: string | null;
    po_number: string;
    created_at: string;
    updated_at: string;
}

export interface AssetUpsertPayload {
    serial_number: string;
    type: 'drill-bit' | 'hammer' | 'drill-rod';
    manufacturer?: string;
    bit_diameter?: number | null;
    description?: string;
    site_id?: string | null;
    status?: 'active' | 'decommissioned' | 'in-transit' | 'regrind';
    total_metres_drilled?: number;
    notes?: string;
    bit_type?: string | null;
    face_design?: string | null;
    po_number?: string;
}

export const assets = {
    list: (params: { site_id?: string; type?: string; limit?: number } = {}) => {
        const qs = new URLSearchParams();
        if (params.site_id) qs.set('site_id', params.site_id);
        if (params.type) qs.set('type', params.type);
        if (params.limit) qs.set('limit', String(params.limit));
        const suffix = qs.toString() ? `?${qs}` : '';
        return request<AssetRow[]>(`/api/assets${suffix}`);
    },
    upsert: (payload: AssetUpsertPayload) =>
        request<AssetRow>('/api/assets', {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    remove: (assetId: string) => request<void>(`/api/assets/${assetId}`, { method: 'DELETE' }),
};

export interface RigRow {
    id: string;
    org_id: string;
    rig_id: string;
    rig_type: 'D65' | 'MD6250' | 'Other';
    manufacturer: string | null;
    serial_number: string | null;
    site_id: string | null;
    status: 'active' | 'decommissioned' | 'maintenance';
    notes: string;
    created_at: string;
    updated_at: string;
}

export interface RigUpsertPayload {
    rig_id: string;
    rig_type: 'D65' | 'MD6250' | 'Other';
    manufacturer?: string | null;
    serial_number?: string | null;
    site_id?: string | null;
    status?: 'active' | 'decommissioned' | 'maintenance';
    notes?: string;
}

export const rigs = {
    list: (params: { site_id?: string; status?: string; limit?: number } = {}) => {
        const qs = new URLSearchParams();
        if (params.site_id) qs.set('site_id', params.site_id);
        if (params.status) qs.set('status', params.status);
        if (params.limit) qs.set('limit', String(params.limit));
        const suffix = qs.toString() ? `?${qs}` : '';
        return request<RigRow[]>(`/api/rigs${suffix}`);
    },
    upsert: (payload: RigUpsertPayload) =>
        request<RigRow>('/api/rigs', {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    remove: (rigId: string) => request<void>(`/api/rigs/${rigId}`, { method: 'DELETE' }),
};

export interface ScanResult {
    shift_details: {
        date?: string;
        shiftType?: string;
        startTime?: string;
        endTime?: string;
        operatorName?: string;
        rigType?: string;
        rigId?: string;
        site?: string;
        location?: string;
        patternId?: string;
    };
    consumables: Array<{
        type?: string;
        manufacturer?: string;
        serialNumber?: string;
        diameter?: string;
        metresDrilled?: number;
    }>;
    delays: Array<{
        delayType?: string;
        timeHours?: number;
        timeMinutes?: number;
        notes?: string;
    }>;
    drill_holes: Array<{
        holeNumber?: string;
        depth?: number;
        diameter?: number;
        angle?: number;
        drillingTimeMinutes?: number;
        notes?: string;
    }>;
    shift_notes: string;
    confidence: {
        overall: string;
        issues: string[];
    };
}

export interface PatternRow {
    id: string;
    pattern_id: string;
    site_id: string;
    file_name: string;
    total_holes: number;
    device_ids: string[];
    created_at: string;
}

export interface PatternDetailRow extends PatternRow {
    pattern_data: Record<string, unknown>;
}

export interface PatternCreatePayload {
    pattern_id: string;
    site_id: string;
    file_name: string;
    pattern_data: Record<string, unknown>;
    total_holes: number;
    device_ids: string[];
}

export const patterns = {
    list: (params: { site_id?: string } = {}) => {
        const qs = new URLSearchParams();
        if (params.site_id) qs.set('site_id', params.site_id);
        const suffix = qs.toString() ? `?${qs}` : '';
        return request<PatternRow[]>(`/api/patterns${suffix}`);
    },
    get: (id: string) => request<PatternDetailRow>(`/api/patterns/${id}`),
    create: (payload: PatternCreatePayload) =>
        request<PatternRow>('/api/patterns', {
            method: 'POST',
            body: JSON.stringify(payload),
        }),
    remove: (id: string) => request<void>(`/api/patterns/${id}`, { method: 'DELETE' }),
    assign: (id: string, deviceIds: string[]) =>
        request<PatternRow>(`/api/patterns/${id}/assign`, {
            method: 'POST',
            body: JSON.stringify({ device_ids: deviceIds }),
        }),
};

export const scan = {
    extract: async (files: File[]): Promise<ScanResult> => {
        const formData = new FormData();
        if (files.length === 1) {
            formData.append('file', files[0]);
            const res = await fetch(`${API_BASE_URL}/api/scan`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new ApiError(res.status, body.detail ?? 'Scan failed', body);
            }
            return res.json();
        }
        for (const f of files) formData.append('files', f);
        const res = await fetch(`${API_BASE_URL}/api/scan/multi`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new ApiError(res.status, body.detail ?? 'Scan failed', body);
        }
        return res.json();
    },
};
